import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BarChart3, RefreshCw, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";

export default function Reports() {
    const { currentOrg } = useAuth();
    const [pl, setPl] = useState(null);
    const [bs, setBs] = useState(null);
    const [ar, setAr] = useState(null);
    const [ap, setAp] = useState(null);
    const [tb, setTb] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [lastLoaded, setLastLoaded] = useState(null);

    const reload = useCallback(async () => {
        if (!currentOrg) return;
        setRefreshing(true);
        try {
            const [a, b, c, d, e] = await Promise.all([
                api.get("/reports/profit-loss"),
                api.get("/reports/balance-sheet"),
                api.get("/reports/aged-receivables"),
                api.get("/reports/aged-payables"),
                api.get("/reports/trial-balance"),
            ]);
            setPl(a.data); setBs(b.data); setAr(c.data); setAp(d.data); setTb(e.data);
            setLastLoaded(new Date());
        } finally {
            setRefreshing(false);
        }
    }, [currentOrg]);

    // Initial + on org switch
    useEffect(() => { reload(); }, [reload]);

    // Refresh on focus / tab visibility — catches user coming back after editing invoices/bills
    useEffect(() => {
        const onFocus = () => reload();
        const onVisibility = () => { if (document.visibilityState === "visible") reload(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        // Light 30s poll — accounting data rarely changes minute-over-minute but keeps Reports "live".
        const iv = setInterval(reload, 30000);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
            clearInterval(iv);
        };
    }, [reload]);

    const currency = currentOrg?.currency || "KYD";

    return (
        <div>
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <PageHeader subtitle="Accounting" title="Reports" />
                <div className="flex items-center gap-3">
                    {lastLoaded && (
                        <span className="text-xs text-muted-foreground" data-testid="reports-last-updated">
                            Updated {lastLoaded.toLocaleTimeString()}
                        </span>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={reload}
                        disabled={refreshing}
                        data-testid="reports-refresh"
                        className="rounded-xl border-sand-300"
                    >
                        {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                        Refresh
                    </Button>
                </div>
            </div>
            <Tabs defaultValue="pl">
                <TabsList className="bg-sand-100 rounded-xl p-1">
                    <TabsTrigger value="pl" data-testid="tab-pl">P&L</TabsTrigger>
                    <TabsTrigger value="bs" data-testid="tab-bs">Balance Sheet</TabsTrigger>
                    <TabsTrigger value="ar" data-testid="tab-ar">Aged AR</TabsTrigger>
                    <TabsTrigger value="ap" data-testid="tab-ap">Aged AP</TabsTrigger>
                    <TabsTrigger value="tb" data-testid="tab-tb">Trial Balance</TabsTrigger>
                </TabsList>

                <TabsContent value="pl" className="mt-6">
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-8 max-w-3xl">
                        <h3 className="font-heading text-2xl text-ocean-700">Profit & Loss</h3>
                        <p className="text-xs text-muted-foreground mt-1">All-time · {currency}</p>
                        <div className="mt-6 divide-y divide-sand-200">
                            <Row label="Revenue" amount={pl?.revenue} currency={currency} bold />
                            <Row label="Cost of goods sold" amount={-(pl?.cost_of_goods || 0)} currency={currency} />
                            <Row label="Gross profit" amount={pl?.gross_profit} currency={currency} bold highlight />
                            {(pl?.operating_expenses || []).map((c) => (
                                <Row key={c.category} label={c.category} amount={-(c.amount || 0)} currency={currency} indent />
                            ))}
                            <Row label="Operating expenses" amount={-(pl?.operating_expense_total || 0)} currency={currency} />
                            <Row label="Net income" amount={pl?.net_income} currency={currency} bold highlight />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="bs" className="mt-6">
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-8 max-w-3xl">
                        <h3 className="font-heading text-2xl text-ocean-700">Balance Sheet</h3>
                        <p className="text-xs text-muted-foreground mt-1">As of {bs ? fmtDate(bs.as_of) : "—"} · {currency}</p>
                        <div className="mt-6">
                            <SectionTitle>Assets</SectionTitle>
                            {(bs?.assets.current || []).map((a) => <Row key={a.name} label={a.name} amount={a.amount} currency={currency} indent />)}
                            <Row label="Total assets" amount={bs?.assets.total} currency={currency} bold highlight />
                            <SectionTitle>Liabilities</SectionTitle>
                            {(bs?.liabilities.current || []).map((a) => <Row key={a.name} label={a.name} amount={a.amount} currency={currency} indent />)}
                            <Row label="Total liabilities" amount={bs?.liabilities.total} currency={currency} bold />
                            <SectionTitle>Equity</SectionTitle>
                            {(bs?.equity.items || []).map((a) => <Row key={a.name} label={a.name} amount={a.amount} currency={currency} indent />)}
                            <Row label="Total equity" amount={bs?.equity.total} currency={currency} bold highlight />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="ar" className="mt-6"><AgedReport data={ar} title="Aged receivables" party="customer" /></TabsContent>
                <TabsContent value="ap" className="mt-6"><AgedReport data={ap} title="Aged payables" party="vendor" /></TabsContent>

                <TabsContent value="tb" className="mt-6">
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 overflow-x-auto">
                        <h3 className="font-heading text-2xl text-ocean-700">Trial balance</h3>
                        <table className="w-full text-sm mt-4">
                            <thead className="text-xs uppercase text-muted-foreground"><tr><th className="text-left py-2">Code</th><th className="text-left py-2">Account</th><th className="text-left py-2">Type</th><th className="text-right py-2">Debit</th><th className="text-right py-2">Credit</th></tr></thead>
                            <tbody className="divide-y divide-sand-200">
                                {(tb?.rows || []).map((r) => (
                                    <tr key={r.code}><td className="py-2 font-mono">{r.code}</td><td className="py-2">{r.name}</td><td className="py-2 text-muted-foreground">{r.type}</td><td className="py-2 font-mono text-right">{r.debit ? fmtMoney(r.debit, currency) : ""}</td><td className="py-2 font-mono text-right">{r.credit ? fmtMoney(r.credit, currency) : ""}</td></tr>
                                ))}
                                <tr className="font-medium text-ocean-700"><td colSpan="3" className="py-3">Totals</td><td className="py-3 font-mono text-right">{fmtMoney(tb?.total_debit || 0, currency)}</td><td className="py-3 font-mono text-right">{fmtMoney(tb?.total_credit || 0, currency)}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Row({ label, amount, currency, bold, indent, highlight }) {
    return (
        <div className={`flex justify-between py-2 ${highlight ? "bg-ocean-50/50 -mx-8 px-8" : ""}`}>
            <div className={`${bold ? "font-medium text-ocean-700" : "text-muted-foreground"} ${indent ? "pl-4" : ""}`}>{label}</div>
            <div className={`font-mono ${bold ? "text-ocean-700 font-medium" : "text-ocean-700"}`}>{fmtMoney(amount || 0, currency)}</div>
        </div>
    );
}

function SectionTitle({ children }) {
    return <div className="text-xs uppercase tracking-[0.18em] text-secondary-dark font-semibold mt-4 mb-2">{children}</div>;
}

function AgedReport({ data, title, party }) {
    if (!data) return null;
    const b = data.buckets || {};
    return (
        <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-6 overflow-x-auto">
            <h3 className="font-heading text-2xl text-ocean-700">{title}</h3>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
                {["current", "1_30", "31_60", "61_90", "90_plus"].map((k, i) => (
                    <div key={k} className="rounded-2xl border border-sand-200 p-4">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{["Current", "1–30 days", "31–60 days", "61–90 days", "90+ days"][i]}</div>
                        <div className="mt-1 font-heading text-xl text-ocean-700">{fmtMoney(b[k] || 0, data.currency)}</div>
                    </div>
                ))}
            </div>
            <table className="w-full text-sm mt-6">
                <thead className="text-xs uppercase text-muted-foreground"><tr><th className="text-left py-2">{party === "customer" ? "Customer" : "Vendor"}</th><th className="text-left py-2">#</th><th className="text-left py-2">Due</th><th className="text-right py-2">Overdue</th><th className="text-right py-2">Balance</th></tr></thead>
                <tbody className="divide-y divide-sand-200">
                    {(data.rows || []).map((r, i) => (
                        <tr key={i}>
                            <td className="py-2 text-ocean-700 font-medium">{r.customer || r.vendor}</td>
                            <td className="py-2 font-mono text-xs">{r.number}</td>
                            <td className="py-2 text-muted-foreground">{fmtDate(r.due_date)}</td>
                            <td className="py-2 text-right">{r.days_overdue} d</td>
                            <td className="py-2 font-mono text-right">{fmtMoney(r.balance, data.currency)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
